// Admin → GDPR deletion queue (Phase 4 Module 2).
//
// Frontend only. Reads GET /api/admin/gdpr/deletion-requests and offers
// per-retailer-closure actions: Export (download), Reactivate (confirm),
// Force delete (type-name confirm — irreversible). Customer requests are
// listed read-only (the retailer actions those via their own record).
//
// Matches the admin portal patterns: adm-table / adm-btn / adm-pill / adm-empty
// / adm-loading globals, the Page + registerPage shape, ctx.api / ctx.showToast,
// and native <dialog> confirms.

import { ApiError } from "@tkos/api-client";
import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

interface RetailerClosure {
  id: string;
  slug: string;
  business_name: string;
  email: string | null;
  deletion_requested_at: string | null;
  deletion_scheduled_at: string | null;
  closed_at: string | null;
}
interface CustomerRequest {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  deletion_requested_at: string | null;
  retailer_id: string;
  retailer_name: string;
  retailer_slug: string;
}
interface QueueResp {
  ok: boolean;
  retailerClosures: RetailerClosure[];
  customerRequests: CustomerRequest[];
}

function esc(s: unknown): string {
  return String(s == null ? "" : s).replace(/[<>&"]/g, (c) =>
    ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c),
  );
}
function fmtDate(s: string | null): string {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB");
}
function statusBadge(status: "pending" | "closed"): string {
  const col = status === "pending" ? "#F59E0B" : "#94a3b8"; // amber / grey
  const label = status === "pending" ? "Pending" : "Closed";
  return `<span class="adm-pill" style="background:${col}22;color:${col}">${label}</span>`;
}

// ── Native confirm dialogs (match the admin <dialog> pattern) ────────────────
function confirmDialog(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    const d = document.createElement("dialog");
    d.innerHTML = `
      <form method="dialog" style="min-width:320px;max-width:440px">
        <p style="margin:0 0 16px;line-height:1.5">${esc(message)}</p>
        <div style="display:flex;gap:8px;justify-content:flex-end">
          <button type="button" class="adm-btn" data-role="cancel">Cancel</button>
          <button type="button" class="adm-btn" data-role="ok">Confirm</button>
        </div>
      </form>`;
    document.body.appendChild(d);
    const done = (v: boolean): void => { try { d.close(); } catch { /* noop */ } d.remove(); resolve(v); };
    d.querySelector<HTMLButtonElement>('[data-role="cancel"]')?.addEventListener("click", () => done(false));
    d.querySelector<HTMLButtonElement>('[data-role="ok"]')?.addEventListener("click", () => done(true));
    d.addEventListener("cancel", () => done(false));
    if (typeof d.showModal === "function") d.showModal(); else d.setAttribute("open", "");
  });
}
function confirmTypeName(message: string, expectedName: string): Promise<boolean> {
  return new Promise((resolve) => {
    const d = document.createElement("dialog");
    d.innerHTML = `
      <form method="dialog" style="min-width:340px;max-width:460px">
        <h3 style="margin:0 0 8px">Force delete — irreversible</h3>
        <p style="margin:0 0 12px;line-height:1.5">${esc(message)}</p>
        <label style="display:block;margin:0 0 14px">
          <span style="display:block;margin-bottom:6px;font-size:0.85rem">Type the retailer name <strong>${esc(expectedName)}</strong> to confirm</span>
          <input type="text" data-role="name" autocomplete="off" style="width:100%;padding:8px 10px" />
        </label>
        <div style="display:flex;gap:8px;justify-content:flex-end">
          <button type="button" class="adm-btn" data-role="cancel">Cancel</button>
          <button type="button" class="adm-btn" data-role="ok" disabled
                  style="background:#ef4444;border-color:#ef4444;color:#fff;opacity:.55">Force delete</button>
        </div>
      </form>`;
    document.body.appendChild(d);
    const input = d.querySelector<HTMLInputElement>('[data-role="name"]');
    const okBtn = d.querySelector<HTMLButtonElement>('[data-role="ok"]');
    input?.addEventListener("input", () => {
      if (!okBtn) return;
      const match = input.value.trim().toLowerCase() === expectedName.trim().toLowerCase() && expectedName.trim() !== "";
      okBtn.disabled = !match;
      okBtn.style.opacity = match ? "1" : ".55";
    });
    const done = (v: boolean): void => { try { d.close(); } catch { /* noop */ } d.remove(); resolve(v); };
    d.querySelector<HTMLButtonElement>('[data-role="cancel"]')?.addEventListener("click", () => done(false));
    okBtn?.addEventListener("click", () => done(true));
    d.addEventListener("cancel", () => done(false));
    if (typeof d.showModal === "function") d.showModal(); else d.setAttribute("open", "");
  });
}

const gdprPage: Page = {
  name: "gdpr",
  label: "GDPR",
  icon: "⚖️",
  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    container.innerHTML = `
      <div style="padding:4px 2px">
        <h1 style="margin:0 0 4px">GDPR — Deletion Requests</h1>
        <p style="margin:0 0 18px;color:var(--color-text-secondary)">Pending retailer account closures and customer deletion requests.</p>
        <div data-role="queue" class="adm-loading">Loading…</div>
      </div>`;
    const host = container.querySelector<HTMLElement>('[data-role="queue"]')!;

    function render(data: QueueResp): void {
      const rows: string[] = [];
      for (const r of data.retailerClosures) {
        const closed = !!r.closed_at;
        const sched = r.deletion_scheduled_at && !closed
          ? ` <span class="adm-pill" style="background:#94a3b822;color:#94a3b8">purge ${esc(fmtDate(r.deletion_scheduled_at))}</span>`
          : "";
        const actions = closed
          ? `<span style="color:var(--color-text-secondary)">Closed</span>`
          : `
            <button class="adm-btn" data-act="export" data-id="${esc(r.id)}">Export</button>
            <button class="adm-btn" data-act="reactivate" data-id="${esc(r.id)}">Reactivate</button>
            <button class="adm-btn" data-act="force-delete" data-id="${esc(r.id)}" data-name="${esc(r.business_name)}"
                    style="background:#ef4444;border-color:#ef4444;color:#fff">Force delete</button>`;
        rows.push(`<tr>
            <td>${esc(r.business_name)}</td>
            <td>Retailer closure</td>
            <td>${esc(fmtDate(r.deletion_requested_at))}</td>
            <td>${statusBadge(closed ? "closed" : "pending")}${sched}</td>
            <td>${actions}</td>
          </tr>`);
      }
      for (const c of data.customerRequests) {
        const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "Customer";
        rows.push(`<tr>
            <td>${esc(name)} <span style="color:var(--color-text-secondary)">· ${esc(c.retailer_name)}</span></td>
            <td>Customer request</td>
            <td>${esc(fmtDate(c.deletion_requested_at))}</td>
            <td>${statusBadge("pending")}</td>
            <td><span style="color:var(--color-text-secondary)">Retailer to action</span></td>
          </tr>`);
      }
      if (!rows.length) {
        host.className = "";
        host.innerHTML = `<div class="adm-empty">No pending deletion requests</div>`;
        return;
      }
      host.className = "";
      host.innerHTML = `
        <table class="adm-table">
          <thead><tr><th>Retailer / Customer</th><th>Type</th><th>Requested</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>${rows.join("")}</tbody>
        </table>`;
      wire();
    }

    function wire(): void {
      host.querySelectorAll<HTMLButtonElement>("[data-act]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const id = btn.dataset["id"] ?? "";
          const act = btn.dataset["act"];
          if (act === "export") { window.open(`/api/admin/gdpr/retailers/${encodeURIComponent(id)}/export`, "_blank"); return; }
          if (act === "reactivate") { void onReactivate(id); return; }
          if (act === "force-delete") { void onForceDelete(id, btn.dataset["name"] ?? ""); return; }
        });
      });
    }

    async function onReactivate(id: string): Promise<void> {
      if (!(await confirmDialog("Reactivate this account and cancel its scheduled closure?"))) return;
      try {
        await ctx.api<unknown>(`/admin/gdpr/retailers/${encodeURIComponent(id)}/reactivate`, { method: "POST" });
        ctx.showToast("Account reactivated — closure cancelled.", "success");
        await load();
      } catch (err) {
        ctx.showToast(err instanceof ApiError && err.status === 409 ? "Not scheduled for closure." : "Could not reactivate.", "error");
      }
    }

    async function onForceDelete(id: string, name: string): Promise<void> {
      const ok = await confirmTypeName(
        `Permanently purge all data for ${name} now. Financial records are retained (anonymised); everything else is deleted. This is IRREVERSIBLE.`,
        name,
      );
      if (!ok) return;
      try {
        await ctx.api<unknown>(`/admin/gdpr/retailers/${encodeURIComponent(id)}/force-delete`, { method: "POST" });
        ctx.showToast("Account permanently purged.", "success");
        await load();
      } catch (err) {
        ctx.showToast(err instanceof ApiError && err.status === 409 ? "Already closed." : "Force delete failed.", "error");
      }
    }

    async function load(): Promise<void> {
      host.className = "adm-loading";
      host.textContent = "Loading…";
      try {
        const d = await ctx.api<QueueResp>("/admin/gdpr/deletion-requests", { signal: ac.signal });
        render(d);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        host.className = "adm-empty";
        host.textContent = "Couldn't load the deletion queue.";
      }
    }
    void load();

    return () => ac.abort();
  },
};

registerPage(gdprPage);

export default gdprPage;
